import { apiGetAuth, apiGetPdfAuth } from '@/utils/api'
import { API_LPD } from '@/utils/api-url'
import { notificationDanger } from '@/utils/notification'

const state = {
  summary: {
    isLoading: false,
    data: {}
  },
  program_position: {
    isLoading: false,
    data: {}
  },
  top_companies: {
    isLoading: false,
    data: {}
  },
  detail: {
    isLoading: false,
    data: {}
  },
  investment_detail: {
    isLoading: false,
    data: {}
  },
  meta_filter: {
    search: '',
    company_id: '',
    program: '',
    page: 1,
    limit: 5,
    order_by: 'transaction_date',
    sort_by: 'desc'
  }
}

const mutations = {
  'SET_LPD_SUMMARY': (state, payload) => {
    state.summary = payload
  },
  'SET_LPD_PROGRAM_POSITION': (state, payload) => {
    state.program_position = payload
  },
  'SET_LPD_TOP_COMPANIES': (state, payload) => {
    state.top_companies = payload
  },
  'SET_LPD_DETAIL': (state, payload) => {
    state.detail = payload
  },
  'SET_LPD_INVESTMENT_DETAIL': (state, payload) => {
    state.investment_detail = payload
  },
  'SET_LPD_META_FILTER': (state, payload) => {
    state.meta_filter = payload
  }
}

// Maps a company payload to a list row: one entry per program with a GroupNbr (program_settings.group_code).
const toLpdCompanyRow = company => ({
  id: company?.id,
  name: company?.name || '-',
  code: company?.code || '-',
  ownership_group_number: company?.ownership_group_number || '-',
  programs: (company?.program_settings || [])
    .filter(item => item?.group_code)
    .map(item => ({ id: item?.id, name: item?.program?.name || '-', group_code: item?.group_code }))
})

const actions = {
  // Companies the logged-in account may open LPD for. The server scopes the
  // list to the account's company access, so only the LPD permission is needed.
  // `search` matches name, code, ownership group number or GroupNbr; `company_id` pins one company.
  async getLpdCompanies(context, payload) {
    const { page = 1, limit = 10, search = '', company_id = '', order_by = '', sort_by = '' } = payload || {}
    const params = { page, limit }
    if (search.trim()) params.search = search.trim()
    if (company_id) params.company_id = company_id
    if (order_by && sort_by) Object.assign(params, { order_by, sort_by })
    try {
      const response = await apiGetAuth(API_LPD.COMPANIES, params)
      return {
        rows: (response?.data?.data || []).map(toLpdCompanyRow),
        total: response?.data?.other?.total || 0
      }
    } catch (err) {
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
      return { rows: [], total: 0, failed: true }
    }
  },
  // One company with its GroupNbr programs, only if the account may access it.
  async getLpdCompany(context, id) {
    try {
      const response = await apiGetAuth(API_LPD.COMPANY_BY_ID(id))
      return { company: toLpdCompanyRow(response?.data?.data) }
    } catch (err) {
      if (err?.response?.status === 403) {
        return { forbidden: true }
      }
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
      return { failed: true }
    }
  },
  async getLpdCompanyReport(context, payload) {
    try {
      const response = await apiGetAuth(API_LPD.COMPANY_REPORT(payload?.companyId, payload?.id), payload?.params)
      return { isLoading: false, data: response?.data }
    } catch (err) {
      notificationDanger(err)
      return { isLoading: false, data: {} }
    }
  },
  // payload.params: { format: 'pdf' | 'xlsx', order_by, sort_by }
  async exportLpdCompanyReport(context, payload) {
    try {
      return await apiGetPdfAuth(API_LPD.COMPANY_REPORT_EXPORT(payload?.companyId, payload?.id), payload?.params)
    } catch (err) {
      notificationDanger(err)
      return null
    }
  },
  async getLpdSummary(context) {
    try {
      context.commit('SET_LPD_SUMMARY', { isLoading: true, data: {} })
      const response = await apiGetAuth(API_LPD.SUMMARY)
      context.commit('SET_LPD_SUMMARY', {
        isLoading: false,
        data: response?.data?.data || {}
      })
    } catch (err) {
      context.commit('SET_LPD_SUMMARY', { isLoading: false, data: {} })
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
    }
  },
  async getLpdProgramPosition(context) {
    try {
      context.commit('SET_LPD_PROGRAM_POSITION', { isLoading: true, data: {} })
      const response = await apiGetAuth(API_LPD.PROGRAM_POSITION)
      context.commit('SET_LPD_PROGRAM_POSITION', {
        isLoading: false,
        data: response?.data?.data || {}
      })
    } catch (err) {
      context.commit('SET_LPD_PROGRAM_POSITION', { isLoading: false, data: {} })
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
    }
  },
  async getLpdTopCompanies(context) {
    try {
      context.commit('SET_LPD_TOP_COMPANIES', { isLoading: true, data: {} })
      const response = await apiGetAuth(API_LPD.TOP_COMPANIES)
      context.commit('SET_LPD_TOP_COMPANIES', {
        isLoading: false,
        data: response?.data?.data || {}
      })
    } catch (err) {
      context.commit('SET_LPD_TOP_COMPANIES', { isLoading: false, data: {} })
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
    }
  },
  async getLpdDetail(context, payload) {
    try {
      context.commit('SET_LPD_DETAIL', { isLoading: true, data: {} })
      const response = await apiGetAuth(API_LPD.DETAIL, payload)
      context.commit('SET_LPD_DETAIL', {
        isLoading: false,
        data: response?.data?.data || {}
      })
    } catch (err) {
      context.commit('SET_LPD_DETAIL', { isLoading: false, data: {} })
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
    }
  },
  async getLpdInvestmentDetail(context, payload) {
    try {
      context.commit('SET_LPD_INVESTMENT_DETAIL', { isLoading: true, data: {} })
      const response = await apiGetAuth(API_LPD.INVESTMENT_DETAIL, payload)
      context.commit('SET_LPD_INVESTMENT_DETAIL', {
        isLoading: false,
        data: response?.data?.data || {}
      })
    } catch (err) {
      context.commit('SET_LPD_INVESTMENT_DETAIL', { isLoading: false, data: {} })
      if (err?.response && err?.response?.data?.status !== 401) {
        notificationDanger(err)
      }
    }
  }
}

const getters = {
  get_lpd_summary: state => {
    return state.summary
  },
  get_lpd_program_position: state => {
    return state.program_position
  },
  get_lpd_top_companies: state => {
    return state.top_companies
  },
  get_lpd_detail: state => {
    return state.detail
  },
  get_lpd_investment_detail: state => {
    return state.investment_detail
  },
  get_lpd_meta_filter: state => {
    return state.meta_filter
  }
}

const namespaced = true
export default {
  namespaced,
  state,
  mutations,
  actions,
  getters
}
